<template>
  <div class="wf-flow-designer">
    <!-- 工具栏 -->
    <div class="wf-toolbar">
      <el-input v-model="flowName" size="small" placeholder="流程名称" style="width: 160px" maxlength="50" />
      <el-input v-model="flowKey" size="small" placeholder="流程标识(flowKey)" style="width: 170px; margin-left: 8px"
                :disabled="!!id" maxlength="50" />
      <span class="wf-required">*</span>
      <el-input v-model="category" size="small" placeholder="分类" style="width: 110px" maxlength="20" />
      <el-autocomplete v-model="subCategory" size="small" style="width: 140px; margin-left: 8px"
                       placeholder="二级分类(选填)" :maxlength="20" clearable
                       :fetch-suggestions="fetchSubCategorySuggestions" @focus="loadSubCategoryOptions" />
      <el-select v-model="formId" size="small" placeholder="绑定表单" style="width: 160px; margin-left: 8px" clearable
                 @change="onFormChange">
        <el-option v-for="f in forms" :key="f.id" :label="f.formName" :value="f.id" />
      </el-select>
      <div class="wf-toolbar-right">
        <el-button size="small" icon="el-icon-view" @click="preview">预览</el-button>
        <el-button size="small" @click="$emit('cancel')">取消</el-button>
        <el-button size="small" type="primary" icon="el-icon-check" :loading="saving" @click="save(false)">保存</el-button>
        <el-button size="small" type="success" icon="el-icon-upload2" :loading="saving" @click="save(true)">保存并发布</el-button>
      </div>
    </div>

    <!-- 节点树画布 -->
    <div class="wf-canvas">
      <node-wrap :node="root" />
    </div>

    <!-- 配置抽屉 -->
    <el-drawer :title="drawerTitle" :visible.sync="drawerVisible" size="420px" append-to-body>
      <div class="wf-drawer-body" v-if="drawerVisible">
        <!-- 审批人 / 抄送人 配置 -->
        <el-form v-if="mode === 'node' && editingNode" label-width="88px" size="small">
          <el-form-item label="节点名称">
            <el-input v-model="editingNode.props.name" placeholder="如：部门审批" maxlength="30" />
          </el-form-item>
          <template v-if="editingNode.type === 'approval' || editingNode.type === 'cc'">
            <el-form-item label="审批人类型">
              <el-select v-model="editingNode.props.assigneeType" style="width: 100%" @change="onAssigneeTypeChange">
                <el-option label="指定人员" value="user" />
                <el-option label="指定角色/岗位" value="role" />
                <el-option label="部门负责人" value="deptLeader" />
                <el-option label="直属上级" value="initiatorLeader" />
                <el-option label="表单字段指定" value="form" />
                <el-option label="表单指定人员的部门负责人" value="formDeptLeader" />
                <el-option label="发起人自选" value="self" />
              </el-select>
            </el-form-item>
            <el-form-item label="选择人员" v-if="editingNode.props.assigneeType === 'user'">
              <el-select v-model="editingNode.props.assigneeValue" multiple filterable style="width: 100%" placeholder="选择人员">
                <el-option v-for="u in users" :key="u.id" :label="u.trueName || u.userName" :value="u.id" />
              </el-select>
            </el-form-item>
            <el-form-item label="选择角色" v-if="editingNode.props.assigneeType === 'role'">
              <el-select v-model="editingNode.props.assigneeValue" multiple filterable style="width: 100%" placeholder="选择角色">
                <el-option v-for="r in roles" :key="r.id" :label="r.roleName" :value="r.id" />
              </el-select>
            </el-form-item>
            <el-form-item
              label="表单字段"
              v-if="editingNode.props.assigneeType === 'form' || editingNode.props.assigneeType === 'formDeptLeader'"
            >
              <el-select
                v-model="editingNode.props.formField"
                style="width: 100%"
                :placeholder="editingNode.props.assigneeType === 'formDeptLeader' ? '选择成员/项目字段' : '选择成员/部门/项目字段'"
                @change="onFormFieldChange"
              >
                <el-option
                  v-for="ff in (editingNode.props.assigneeType === 'formDeptLeader' ? formLeaderFieldOptions : formFieldOptions)"
                  :key="ff.key"
                  :label="ff.label"
                  :value="ff.key"
                />
              </el-select>
              <div class="wf-hint" v-if="editingNode.props.assigneeType === 'formDeptLeader'">
                可选「成员/当前用户id/项目」字段；项目字段将解析为项目归属销售，再取其所在部门的负责人
              </div>
              <div class="wf-hint" v-else>可选「成员/部门/当前用户id/项目」字段；项目字段将解析为项目归属销售（立项申请人）</div>
            </el-form-item>
            <template v-if="editingNode.type === 'approval'">
              <el-form-item label="审批方式">
                <el-radio-group v-model="editingNode.props.approveMode">
                  <el-radio label="or">或签(任一同意)</el-radio>
                  <el-radio label="and">会签(全部同意)</el-radio>
                </el-radio-group>
              </el-form-item>
              <el-form-item label="发起人自动通过">
                <el-switch v-model="editingNode.props.skipInitiator" />
                <span class="wf-hint" style="margin-left:8px">发起人同时是审批人时自动同意(或签直接通过)</span>
              </el-form-item>
              <el-form-item label="重复审批人自动通过">
                <el-switch v-model="editingNode.props.skipRepeatedApprover" />
                <span class="wf-hint" style="margin-left:8px">已在前面节点通过的审批人，本节点自动同意</span>
              </el-form-item>
              <el-form-item label="无审批人时">
                <el-select v-model="editingNode.props.emptyStrategy" style="width: 100%">
                  <el-option label="自动通过" value="autoPass" />
                  <el-option label="转系统管理员" value="assignAdmin" />
                  <el-option label="阻断(报错)" value="block" />
                </el-select>
              </el-form-item>
              <el-form-item label="拒绝时">
                <el-select v-model="editingNode.props.rejectStrategy" style="width: 100%">
                  <el-option label="直接结束流程" value="end" />
                </el-select>
              </el-form-item>
            </template>
          </template>
          <el-form-item v-if="editingNode.type === 'start'" label="发起范围">
            <el-select v-model="editingNode.props.initiatorScope" style="width: 100%">
              <el-option label="全员可发起" value="all" />
            </el-select>
          </el-form-item>
        </el-form>

        <!-- 条件分支配置 -->
        <el-form v-else-if="mode === 'branch' && editingBranch" label-width="72px" size="small">
          <el-form-item label="分支名称">
            <el-input v-model="editingBranch.branch.name" placeholder="如：金额>10万" maxlength="30" />
          </el-form-item>
          <el-form-item label="默认分支">
            <el-switch :value="!editingBranch.branch.condition" @input="toggleDefault" />
            <span class="wf-hint" style="margin-left:8px">开启表示「其它情况」兜底分支</span>
          </el-form-item>
          <template v-if="editingBranch.branch.condition">
            <el-form-item label="条件关系">
              <el-radio-group v-model="editingBranch.branch.condition.logic" size="mini">
                <el-radio-button label="and">且(and)</el-radio-button>
                <el-radio-button label="or">或(or)</el-radio-button>
              </el-radio-group>
            </el-form-item>
            <el-form-item label="条件">
              <div v-for="(r, i) in editingBranch.branch.condition.rules" :key="i" class="wf-rule-row">
                <el-select v-model="r.field" size="mini" placeholder="字段" style="width: 108px">
                  <el-option v-for="ff in condFields" :key="ff.key" :label="ff.label" :value="ff.key" />
                </el-select>
                <el-select v-model="r.op" size="mini" placeholder="操作" style="width: 92px">
                  <el-option v-for="op in ops" :key="op.value" :label="op.label" :value="op.value" />
                </el-select>
                <el-input v-if="!['empty','notEmpty'].includes(r.op)" v-model="r.value" size="mini" placeholder="值" style="width: 92px" />
                <i class="el-icon-delete wf-rule-del" @click="editingBranch.branch.condition.rules.splice(i,1)"></i>
              </div>
              <el-button size="mini" icon="el-icon-plus" @click="addRule">添加条件</el-button>
              <div class="wf-hint">字段来自绑定表单，请确保已绑定表单</div>
            </el-form-item>
          </template>
        </el-form>
      </div>
    </el-drawer>

    <!-- 预览 -->
    <el-dialog title="流程预览(按当前表单数据的默认走向)" :visible.sync="previewVisible" width="520px" append-to-body>
      <flow-viewer :flow-json="root" :instance="previewInstance" :tasks="[]" />
    </el-dialog>
  </div>
</template>

<script>
import NodeWrap from './components/designer/flow/NodeWrap.vue'
import FlowViewer from './components/FlowViewer.vue'
import { flatten } from './components/flowUtil'
import { CONDITION_OPS } from './components/designer/form/fieldTypes'
import { getFlow, saveFlow, publishFlow, listSubCategories } from '@/api/wflow/flow'
import { listAllForm, getForm } from '@/api/wflow/form'
import { getUserList } from '@/api/plat/PlatStaff'
import { getTRoleSelectByPage } from '@/api/plat/PosMana'

export default {
  name: 'WflowFlowDesigner',
  components: { NodeWrap, FlowViewer },
  props: {
    flowId: { type: [Number, String], default: null }
  },
  provide() {
    return { wfDesigner: this }
  },
  data() {
    return {
      id: null, flowName: '', flowKey: '', category: '', subCategory: '', formId: null, remark: '',
      status: 0, version: 0,
      subCategoryOptions: [], subCategoryOptionsFor: null,
      root: this.newTree(),
      forms: [], users: [], roles: [],
      boundFormFields: [],
      drawerVisible: false, mode: 'node', editingNode: null, editingBranch: null,
      ops: CONDITION_OPS,
      saving: false,
      previewVisible: false,
      previewInstance: { status: 0, formData: '{}', initiatorName: '预览', currentNodeKey: null }
    }
  },
  computed: {
    drawerTitle() {
      if (this.mode === 'branch') return '条件分支配置'
      const t = this.editingNode && this.editingNode.type
      return { start: '发起人设置', approval: '审批人设置', cc: '抄送人设置' }[t] || '节点设置'
    },
    condFields() {
      // 明细/表格值为行数组，不参与流程条件分支
      return this.boundFormFields.filter(f => f.type !== 'tips' && f.type !== 'detail')
    },
    // 表单字段指定（form）：成员/部门/当前用户id/项目
    formFieldOptions() {
      return this.boundFormFields.filter(f => ['user', 'dept', 'curUser', 'project'].indexOf(f.type) >= 0)
    },
    // 表单指定人员的部门负责人（formDeptLeader）：成员/当前用户id/项目
    formLeaderFieldOptions() {
      return this.boundFormFields.filter(f => ['user', 'curUser', 'project'].indexOf(f.type) >= 0)
    }
  },
  watch: {
    // immediate 保证组件创建时即按 flowId 加载；prop 变化时也能重新加载
    flowId: {
      immediate: true,
      handler(val) { if (val) this.load(val) }
    },
    // 主分类变化：二级分类候选需按新的主分类重新拉取
    category(val, oldVal) {
      if ((val || '').trim() === (oldVal || '').trim()) return
      this.subCategoryOptionsFor = null
      this.subCategoryOptions = []
    }
  },
  created() {
    this.loadForms()
    this.loadUsers()
    this.loadRoles()
  },
  methods: {
    /* ---------- 初始化/加载 ---------- */
    newTree() {
      return {
        type: 'start', nodeKey: 'start', props: { initiatorScope: 'all' },
        child: { type: 'end', nodeKey: 'end', props: {} }
      }
    },
    loadForms() {
      listAllForm().then(res => { if (res && res.code === 200) this.forms = res.data || [] })
    },
    loadUsers() {
      getUserList({ page: 1, limit: 2000, isValid: 1 }).then(res => {
        if (res && res.code === 200 && res.data) this.users = res.data.records || res.data || []
      })
    },
    loadRoles() {
      getTRoleSelectByPage({ page: 1, limit: 999, roleName: '' }).then(res => {
        if (res && res.code === 200) this.roles = res.data || []
      })
    },
    load(id) {
      getFlow(id).then(res => {
        if (res && res.code === 200 && res.data) {
          const d = res.data
          this.id = d.id; this.flowName = d.flowName; this.flowKey = d.flowKey
          this.category = d.category; this.subCategory = d.subCategory || ''
          this.formId = d.formId; this.remark = d.remark
          this.status = d.status; this.version = d.version
          if (d.flowJson) { try { this.root = JSON.parse(d.flowJson) } catch (e) { this.root = this.newTree() } }
          if (this.formId) this.loadBoundForm(this.formId)
        } else {
          this.$message.error((res && res.msg) || '加载流程失败，请刷新页面后重试')
        }
      }).catch(() => {
        this.$message.error('加载流程失败：网络异常或登录已失效，请刷新页面后重试')
      })
    },
    onFormChange(id) {
      this.boundFormFields = []
      if (id) this.loadBoundForm(id)
    },
    loadBoundForm(id) {
      getForm(id).then(res => {
        if (res && res.code === 200 && res.data && res.data.formJson) {
          try {
            const schema = JSON.parse(res.data.formJson)
            this.boundFormFields = Array.isArray(schema.fields) ? schema.fields : []
          } catch (e) { this.boundFormFields = [] }
        }
      })
    },
    // 二级分类建议：按当前主分类拉取已用过的值（同一分类只拉一次）
    loadSubCategoryOptions() {
      const cat = (this.category || '').trim()
      if (this.subCategoryOptionsFor === cat) return Promise.resolve(this.subCategoryOptions)
      this.subCategoryOptionsFor = cat
      return listSubCategories(cat).then(res => {
        this.subCategoryOptions = (res && res.code === 200 && res.data) ? res.data : []
        return this.subCategoryOptions
      }).catch(() => {
        this.subCategoryOptions = []
        return []
      })
    },
    // el-autocomplete 取建议：未加载过当前分类的候选时先拉取再回调
    fetchSubCategorySuggestions(queryString, cb) {
      const kw = (queryString || '').trim().toLowerCase()
      const done = list => cb((list || [])
        .filter(v => !kw || String(v).toLowerCase().indexOf(kw) > -1)
        .map(v => ({ value: v })))
      if (this.subCategoryOptionsFor !== (this.category || '').trim()) {
        this.loadSubCategoryOptions().then(done)
        return
      }
      done(this.subCategoryOptions)
    },

    /* ---------- 树操作（供 NodeWrap 注入调用） ---------- */
    genKey(type) {
      return (type || 'n').charAt(0) + '_' + Date.now().toString(36).slice(-5) + Math.floor(Math.random() * 90 + 10)
    },
    createNode(type) {
      const nodeKey = this.genKey(type)
      if (type === 'approval') {
        return { type, nodeKey, props: { name: '审批人', assigneeType: 'user', assigneeValue: [], approveMode: 'or', emptyStrategy: 'autoPass', rejectStrategy: 'end', skipInitiator: true, skipRepeatedApprover: true }, child: null }
      }
      if (type === 'cc') {
        return { type, nodeKey, props: { name: '抄送人', assigneeType: 'user', assigneeValue: [] }, child: null }
      }
      if (type === 'branch') {
        return {
          type, nodeKey, props: { name: '条件分支' },
          branches: [
            { name: '条件1', condition: { logic: 'and', rules: [] }, child: null },
            { name: '其它情况', condition: null, child: null }
          ],
          child: null
        }
      }
      return { type, nodeKey, props: {}, child: null }
    },
    insertAfter(node, type) {
      const nn = this.createNode(type)
      nn.child = node.child
      this.$set(node, 'child', nn)
    },
    insertIntoBranch(branch, type) {
      const nn = this.createNode(type)
      if (!branch.child) { this.$set(branch, 'child', nn); return }
      let tail = branch.child
      while (tail.child) tail = tail.child
      nn.child = tail.child
      this.$set(tail, 'child', nn)
    },
    removeNode(node, parent, branch) {
      if (branch) { this.$set(branch, 'child', node.child) }
      else if (parent) { this.$set(parent, 'child', node.child) }
    },
    addBranch(branchNode) {
      if (!branchNode.branches) this.$set(branchNode, 'branches', [])
      const n = branchNode.branches.length + 1
      branchNode.branches.splice(branchNode.branches.length - 1, 0, { name: '条件' + n, condition: { logic: 'and', rules: [] }, child: null })
    },
    removeBranch(branchNode, index) {
      branchNode.branches.splice(index, 1)
    },

    /* ---------- 配置抽屉 ---------- */
    openConfig(node) {
      if (!node.props) this.$set(node, 'props', {})
      // 审批节点：发起人自动通过默认开启（兼容旧流程 JSON 缺省）
      if (node.type === 'approval' && node.props.skipInitiator === undefined) {
        this.$set(node.props, 'skipInitiator', true)
      }
      // 审批节点：重复审批人自动通过默认开启（兼容旧流程 JSON 缺省）
      if (node.type === 'approval' && node.props.skipRepeatedApprover === undefined) {
        this.$set(node.props, 'skipRepeatedApprover', true)
      }
      this.mode = 'node'; this.editingNode = node; this.editingBranch = null
      this.drawerVisible = true
    },
    openBranchConfig(branchNode, branch, index) {
      this.mode = 'branch'; this.editingBranch = { branchNode, branch, index }; this.editingNode = null
      this.drawerVisible = true
    },
    onAssigneeTypeChange() {
      const p = this.editingNode.props
      if (p.assigneeType !== 'user' && p.assigneeType !== 'role') p.assigneeValue = []
      if (p.assigneeType !== 'form' && p.assigneeType !== 'formDeptLeader') {
        this.$set(p, 'formField', '')
        this.$set(p, 'formFieldType', '')
      }
    },
    // 记录所选表单字段的类型（project 需解析为归属销售）
    onFormFieldChange(key) {
      const p = this.editingNode && this.editingNode.props
      if (!p) return
      const f = this.boundFormFields.find(x => x.key === key)
      this.$set(p, 'formFieldType', f ? f.type : '')
    },
    toggleDefault(val) {
      // val=true 表示设为默认分支(condition=null)
      this.$set(this.editingBranch.branch, 'condition', val ? null : { logic: 'and', rules: [] })
    },
    addRule() {
      const cond = this.editingBranch.branch.condition
      if (!cond.rules) this.$set(cond, 'rules', [])
      cond.rules.push({ field: this.condFields.length ? this.condFields[0].key : '', op: '=', value: '' })
    },

    /* ---------- 预览/保存/发布 ---------- */
    preview() {
      this.previewVisible = true
    },
    validate() {
      if (!this.flowName || !this.flowName.trim()) { this.$message.warning('请填写流程名称'); return false }
      if (!this.flowKey || !this.flowKey.trim()) { this.$message.warning('请填写流程标识 flowKey'); return false }
      if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(this.flowKey)) { this.$message.warning('flowKey 需字母开头，仅含字母/数字/下划线'); return false }
      if (!this.category || !this.category.trim()) { this.$message.warning('请填写流程分类'); return false }
      const all = flatten(this.root)
      const keys = {}
      for (const n of all) {
        if (!n.nodeKey) { this.$message.warning('存在节点缺少 nodeKey'); return false }
        if (keys[n.nodeKey]) { this.$message.warning('节点标识重复：' + n.nodeKey); return false }
        keys[n.nodeKey] = true
        if ((n.type === 'approval' || n.type === 'cc') && n.props) {
          const at = n.props.assigneeType
          if (!at) { this.$message.warning('节点「' + (n.props.name || n.nodeKey) + '」未选择审批人类型'); return false }
          if ((at === 'user' || at === 'role') && (!n.props.assigneeValue || !n.props.assigneeValue.length)) {
            this.$message.warning('节点「' + (n.props.name || n.nodeKey) + '」未选择人员/角色'); return false
          }
          if ((at === 'form' || at === 'formDeptLeader') && !n.props.formField) { this.$message.warning('节点「' + (n.props.name || n.nodeKey) + '」未选择表单字段'); return false }
        }
      }
      return true
    },
    save(andPublish) {
      if (!this.validate()) return
      this.saving = true
      const payload = {
        id: this.id, flowKey: this.flowKey.trim(), flowName: this.flowName.trim(),
        category: this.category.trim(), subCategory: (this.subCategory || '').trim(),
        formId: this.formId, remark: this.remark,
        status: this.status, version: this.version,
        flowJson: JSON.stringify(this.root)
      }
      saveFlow(payload).then(res => {
        if (!res || res.code !== 200) { this.saving = false; return }
        if (res.data && res.data.id) this.id = res.data.id
        if (andPublish) {
          return publishFlow(this.id).then(pr => {
            this.saving = false
            if (pr && pr.code === 200) { this.$message.success('已保存并发布'); this.$emit('saved') }
          })
        }
        this.saving = false
        this.$message.success('保存成功')
        this.$emit('saved')
      }).catch(() => { this.saving = false })
    }
  }
}
</script>

<style scoped>
.wf-flow-designer { display: flex; flex-direction: column; height: 100%; }
.wf-toolbar {
  display: flex; align-items: center; padding: 10px 12px;
  border-bottom: 1px solid #EBEEF5; background: #fff; flex-wrap: wrap; gap: 4px 0;
}
.wf-toolbar-right { margin-left: auto; }
.wf-canvas {
  flex: 1; overflow: auto; background: #F5F7FA;
  padding: 30px 20px; display: flex; justify-content: center;
}
.wf-drawer-body { padding: 0 16px 24px; }
.wf-hint { color: #C0C4CC; font-size: 12px; line-height: 1.4; }
.wf-required { color: #F56C6C; margin-left: 8px; margin-right: 2px; }
.wf-rule-row { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.wf-rule-del { color: #F56C6C; cursor: pointer; }
</style>
